import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { Avatar } from "../components/Avatar";
import { Button } from "@/components/ui/button";
import { Check, X, ShieldCheck, ArrowRight, UserPlus, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function Guardian() {
  const navigate = useNavigate();
  const [minors, setMinors] = useState(null);
  const [items, setItems] = useState([]);
  const [signups, setSignups] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    const [m, a, b] = await Promise.all([
      api.get("/guardian/minors").catch(() => ({ data: [] })),
      api.get("/guardian/approvals").catch(() => ({ data: [] })),
      api.get("/guardian/signup-approvals").catch(() => ({ data: [] })),
    ]);
    setMinors(m.data);
    setItems(a.data);
    setSignups(b.data);
    setSelectedId((prev) => prev || (m.data[0] && m.data[0].id) || null);
  };
  useEffect(() => { load(); }, []);

  const respond = async (request_id, action) => {
    try { await api.post("/guardian/respond", { request_id, action }); toast.success(action === "approve" ? "Approved" : "Denied"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const respondSignup = async (id, action) => {
    try { await api.post("/guardian/signup-respond", { id, action }); toast.success(action === "approve" ? "Member approved — they can now log in" : "Signup denied"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  if (minors === null) return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>;

  const mySignups = signups.filter((s) => s.minor?.id === selectedId);
  const myItems = items.filter((r) => r.minor?.id === selectedId);
  const outgoing = myItems.filter((r) => r.direction === "outgoing");
  const incoming = myItems.filter((r) => r.direction !== "outgoing");
  const selected = minors.find((m) => m.id === selectedId);

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="font-display text-3xl font-bold text-spotter-maroon mb-1 flex items-center gap-2"><ShieldCheck className="w-7 h-7" />Guardian Dashboard</h2>
      <p className="text-spotter-maroon mb-5">Manage each teen (ages 16–17) who named you as their parent/guardian — one workspace per child.</p>

      {minors.length === 0 ? (
        <div className="bg-white border-2 border-black rounded-xl p-6 text-center text-spotter-maroon" data-testid="no-minors">
          No teens are linked to your account yet. When a 16–17 year old signs up and lists your username or email, they'll appear here for approval.
        </div>
      ) : (
        <>
          {/* Minor switcher */}
          <div className="bg-white border-2 border-black rounded-xl p-3 mb-5 brand-shadow-sm" data-testid="minor-switcher">
            <div className="text-xs font-bold text-spotter-maroon mb-2 flex items-center gap-1"><Users className="w-4 h-4" />Switch view</div>
            <div className="flex gap-2 flex-wrap">
              {minors.map((m) => {
                const isSel = m.id === selectedId;
                return (
                  <button key={m.id} data-testid={`minor-tab-${m.id}`} onClick={() => setSelectedId(m.id)}
                    className={`relative flex items-center gap-2 border-2 border-black rounded-full pl-1 pr-3 py-1 transition-colors ${isSel ? "bg-spotter-maroon text-spotter-cream" : "bg-spotter-cream text-spotter-maroon hover:bg-spotter-gold/30"}`}>
                    <Avatar user={m} size="sm" />
                    <span className="text-sm font-bold">{m.name}</span>
                    {m.pending_count > 0 && <span className="min-w-[18px] h-[18px] px-1 text-[11px] font-bold rounded-full bg-red-600 text-white flex items-center justify-center border border-black" data-testid={`minor-pending-${m.id}`}>{m.pending_count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {selected && (
            <div key={selectedId} data-testid={`minor-workspace-${selectedId}`}>
              <div className="flex items-center gap-2 mb-4">
                <Avatar user={selected} size="md" />
                <div>
                  <div className="font-display text-xl font-bold text-spotter-maroon">{selected.name}'s workspace</div>
                  <div className="text-xs text-spotter-maroon">@{selected.username} · {selected.signup_status === "pending" ? "Awaiting your sign-up approval" : "Active member"}</div>
                </div>
              </div>

              <h3 className="font-display text-lg font-bold text-spotter-maroon mb-2 flex items-center gap-2"><UserPlus className="w-5 h-5" />Sign-up approval</h3>
              {mySignups.length === 0 ? <p className="text-spotter-maroon mb-6 text-sm">No sign-up waiting for this teen.</p> : (
                <div className="space-y-3 mb-8">
                  {mySignups.map((s) => (
                    <div key={s.id} className="bg-white border-2 border-black rounded-xl p-4" data-testid={`signup-approval-${s.id}`}>
                      <p className="text-xs text-spotter-maroon mb-3"><b>{s.minor.name}</b> is signing up and listed you as their parent/guardian. They can't log in until you approve.</p>
                      <div className="flex gap-2">
                        <Button data-testid={`signup-approve-${s.id}`} onClick={() => respondSignup(s.id, "approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
                        <Button data-testid={`signup-deny-${s.id}`} onClick={() => respondSignup(s.id, "deny")} variant="outline" className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <h3 className="font-display text-lg font-bold text-spotter-maroon mb-2 flex items-center gap-2"><ArrowRight className="w-5 h-5" />Friends {selected.name} wants to add</h3>
              {outgoing.length === 0 ? <p className="text-spotter-maroon text-sm mb-6">No pending outgoing requests.</p> : (
                <div className="space-y-3 mb-8">
                  {outgoing.map((r) => (
                    <div key={r.request_id} className="bg-white border-2 border-black rounded-xl p-4" data-testid={`approval-out-${r.request_id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <button onClick={() => navigate(`/profile/${r.to_user.id}`)} className="flex items-center gap-2"><Avatar user={r.to_user} size="sm" /><span className="font-bold text-sm">{r.to_user.name} <span className="text-xs text-spotter-maroon">@{r.to_user.username}</span></span></button>
                      </div>
                      <p className="text-xs text-spotter-maroon mb-3">Review the profile, then approve or deny this request.</p>
                      <div className="flex gap-2">
                        <Button data-testid={`approve-${r.request_id}`} onClick={() => respond(r.request_id, "approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
                        <Button data-testid={`deny-${r.request_id}`} onClick={() => respond(r.request_id, "deny")} variant="outline" className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <h3 className="font-display text-lg font-bold text-spotter-maroon mb-2 flex items-center gap-2"><ArrowRight className="w-5 h-5" />Friend requests {selected.name} received</h3>
              {incoming.length === 0 ? <p className="text-spotter-maroon text-sm">No pending incoming requests.</p> : (
                <div className="space-y-3">
                  {incoming.map((r) => (
                    <div key={r.request_id} className="bg-white border-2 border-black rounded-xl p-4" data-testid={`approval-in-${r.request_id}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <button onClick={() => navigate(`/profile/${r.from_user.id}`)} className="flex items-center gap-2"><Avatar user={r.from_user} size="sm" /><span className="font-bold text-sm">{r.from_user.name} <span className="text-xs text-spotter-maroon">@{r.from_user.username}</span></span></button>
                      </div>
                      <div className="flex gap-2">
                        <Button data-testid={`approve-${r.request_id}`} onClick={() => respond(r.request_id, "approve")} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-1"><Check className="w-4 h-4" />Approve</Button>
                        <Button data-testid={`deny-${r.request_id}`} onClick={() => respond(r.request_id, "deny")} variant="outline" className="border-2 border-black gap-1"><X className="w-4 h-4" />Deny</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
